import React, {useEffect, useRef, useState} from 'react';
import post from '../utils/post.js';
import AppHeader from '../components/AppHeader.js';
import Search from '../components/Search.js';
import WorkoutNew from '../components/WorkoutNew.js';
import WorkoutEdit from '../components/WorkoutEdit.js';
import ConfirmPopup from '../components/ConfirmPopup.js';
import MultiMuscleBadge, {getWorkoutMusclesMap} from '../components/MultiMuscleBadge.js';

const formatGroupCount = (count) => {
	if (count === 1) {
		return '1 cvik';
	}

	if (count >= 2 && count <= 4) {
		return `${count} cviky`;
	}

	return `${count} cviků`;
};

const formatWorkoutName = (item) => {
	const recOrder = parseInt(item.REC_ORDER, 10);

	if (Number.isInteger(recOrder) && recOrder > 0) {
		return `${recOrder}. ${item.NAME}`;
	}

	return item.NAME;
};

const getGroupKey = (muscleId) => String(parseInt(muscleId, 10) || 0);

const reorderList = (items, fromIndex, toIndex, assignOrder = true) => {
	const nextItems = [...items];
	const [movedItem] = nextItems.splice(fromIndex, 1);
	nextItems.splice(toIndex, 0, movedItem);

	if (!assignOrder) {
		return nextItems;
	}

	return nextItems.map((item, index) => ({
		...item,
		REC_ORDER: index + 1,
	}));
};

const getIndexFromPointer = (clientY, rowElements) => {
	for (let index = 0; index < rowElements.length; index += 1) {
		const rowElement = rowElements[index];

		if (!rowElement) {
			continue;
		}

		const rect = rowElement.getBoundingClientRect();
		const midpoint = rect.top + rect.height / 2;

		if (clientY < midpoint) {
			return index;
		}
	}

	return Math.max(rowElements.length - 1, 0);
};

const getWorkoutRowsFromResponse = (responseData) => {
	if (Array.isArray(responseData)) {
		return responseData;
	}

	if (Array.isArray(responseData?.data)) {
		return responseData.data;
	}

	if (Array.isArray(responseData?.data?.data)) {
		return responseData.data.data;
	}

	return null;
};

const getGroupDisplayItems = (group, activeDragState) => {
	if (activeDragState?.muscleId === group.muscleId) {
		return reorderList(group.items, activeDragState.fromIndex, activeDragState.overIndex, false);
	}

	return group.items;
};

export default function Workout({usrInfo, timerCnt}) {
	const [loading, setLoading] = useState(false);
	const [data, setData] = useState([]);
	const [targetUsrName, setTargetUsrName] = useState('');
	const [dataMuscles, setDataMuscles] = useState([]);
	const [dataWorkoutOffic, setDataWorkoutOffic] = useState([]);
	const [dataDetail, setDataDetail] = useState({});
	const [searchValue, setSearchValue] = useState('');
	const [showNew, setShowNew] = useState(false);
	const [showEdit, setShowEdit] = useState(false);
	const [targetUsrId, setTargetUsrId] = useState(0);
	const [showImportPopup, setShowImportPopup] = useState(false);
	const [importSearchValue, setImportSearchValue] = useState('');
	const [importLoading, setImportLoading] = useState(false);
	const [importSaving, setImportSaving] = useState(false);
	const [importData, setImportData] = useState([]);
	const [selectedImportIds, setSelectedImportIds] = useState({});
	const [dragState, setDragState] = useState(null);
	const dragStateRef = useRef(null);
	const rowRefs = useRef({});
	const dragListenersRef = useRef(null);

	const clearDragListeners = () => {
		if (!dragListenersRef.current) {
			return;
		}

		const {onMove, onUp} = dragListenersRef.current;
		window.removeEventListener('pointermove', onMove);
		window.removeEventListener('pointerup', onUp);
		window.removeEventListener('pointercancel', onUp);
		dragListenersRef.current = null;
	};

	useEffect(() => {
		dragStateRef.current = dragState;
	}, [dragState]);

	useEffect(() => {
		return () => {
			clearDragListeners();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const pathUsrId = parseInt(window.location.pathname.split('/')[2], 10);
		const nextTargetUsrId = Number.isInteger(pathUsrId) && pathUsrId > 0 ? pathUsrId : 0;

		setTargetUsrId(nextTargetUsrId);
		getData('', nextTargetUsrId);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const getData = (search, requestedUsrId = targetUsrId) => {
		setLoading(true);
		const payload = {search: search};

		if (requestedUsrId > 0) {
			payload.USRID = requestedUsrId;
		}

		post('getListWorkout', payload)
			.then((res) => {
				setData(getWorkoutRowsFromResponse(res) || []);
				setDataMuscles(res.data?.data_muscles || []);
				setDataWorkoutOffic(res.data?.data_workoutoffic || []);
				setTargetUsrName(res.data?.target_usr_name || '');
				setLoading(false);
			})
			.catch((error) => {
				console.error(error);
				setLoading(false);
			});
	};

	const handleNew = () => {
		setShowNew(true);
	};

	const isInstructorClientView = targetUsrId > 0 && targetUsrId !== (parseInt(usrInfo.usrId, 10) || 0);

	const closeNew = () => {
		setShowNew(false);
	};

	const applyWorkoutList = (responseData) => {
		const rows = getWorkoutRowsFromResponse(responseData);

		if (Array.isArray(rows)) {
			setData(rows);
		}
	};

	const updateWorkoutRowNew = (res) => {
		applyWorkoutList(res);
	};

	const handleEdit = (rec) => {
		setShowEdit(true);
		setDataDetail(rec);
	};

	const closeEdit = () => {
		setShowEdit(false);
	};

	const updateWorkoutRowEdit = (res) => {
		applyWorkoutList(res);
	};

	const updateWorkoutRowDelete = (res) => {
		applyWorkoutList(res);
	};

	const openImportPopup = () => {
		setImportSearchValue('');
		setSelectedImportIds({});
		setImportLoading(true);
		setShowImportPopup(true);

		post('getListWorkout', {search: ''})
			.then((res) => {
				setImportData(res.data.data || []);
				setImportLoading(false);
			})
			.catch((error) => {
				console.error(error);
				setImportLoading(false);
			});
	};

	const closeImportPopup = () => {
		if (importSaving) {
			return;
		}

		setShowImportPopup(false);
		setImportSearchValue('');
		setSelectedImportIds({});
	};

	const resetImportPopup = () => {
		setShowImportPopup(false);
		setImportSearchValue('');
		setSelectedImportIds({});
	};

	const toggleImportSelection = (workoutId) => {
		setSelectedImportIds((prevState) => {
			const newState = {...prevState};

			if (newState[workoutId]) {
				delete newState[workoutId];
			} else {
				newState[workoutId] = true;
			}

			return newState;
		});
	};

	const handleImportRowToggle = (workoutId) => {
		if (importSaving) {
			return;
		}

		toggleImportSelection(workoutId);
	};

	const handleImportGroupToggle = (groupItems) => {
		if (importSaving) {
			return;
		}

		const groupWorkoutIds = groupItems
			.map((item) => parseInt(item.ID, 10) || 0)
			.filter((workoutId) => workoutId > 0);

		if (groupWorkoutIds.length === 0) {
			return;
		}

		setSelectedImportIds((prevState) => {
			const nextState = {...prevState};
			const areAllSelected = groupWorkoutIds.every((workoutId) => Boolean(nextState[workoutId]));

			groupWorkoutIds.forEach((workoutId) => {
				if (areAllSelected) {
					delete nextState[workoutId];
				} else {
					nextState[workoutId] = true;
				}
			});

			return nextState;
		});
	};

	const importedSourceIdMap = data.reduce((acc, item) => {
		const sourceId = parseInt(item.SOURCEID, 10) || 0;

		if (sourceId > 0) {
			acc[sourceId] = true;
		}

		return acc;
	}, {});

	const filteredImportData = importData.filter((item) => {
		const workoutId = parseInt(item.ID, 10) || 0;
		const normalizedSearch = importSearchValue.trim().toLowerCase();

		if (workoutId > 0 && importedSourceIdMap[workoutId]) {
			return false;
		}

		if (normalizedSearch === '') {
			return true;
		}

		return String(item.NAME || '').toLowerCase().includes(normalizedSearch) || String(item.MUSCLE_NAME || '').toLowerCase().includes(normalizedSearch);
	});

	const importMusclesMap = getWorkoutMusclesMap(importData);

	const groupedImportData = filteredImportData.reduce((groups, item) => {
		const muscleId = parseInt(item.MUSCLEID, 10) || 0;
		const lastGroup = groups[groups.length - 1];

		if (lastGroup && lastGroup.muscleId === muscleId) {
			lastGroup.items.push(item);
			return groups;
		}

		groups.push({
			muscleId,
			muscleName: item.MUSCLE_NAME || 'Bez svalové partie',
			accentColor: item.COLOR,
			items: [item],
		});

		return groups;
	}, []);

	const selectedSourceIds = Object.keys(selectedImportIds)
		.filter((key) => selectedImportIds[key])
		.map((key) => parseInt(key, 10))
		.filter((key) => Number.isInteger(key) && key > 0);

	const handleImport = () => {
		if (selectedSourceIds.length === 0) {
			return;
		}

		setImportSaving(true);

		post('importWorkout', {USRID: targetUsrId, SOURCEIDS: selectedSourceIds})
			.then(() => {
				resetImportPopup();
				setImportSaving(false);
				getData('', targetUsrId);
			})
			.catch((error) => {
				console.error(error);
				setImportSaving(false);
			});
	};

	const onChangeSearch = (value) => {
		setSearchValue(value);
	};

	const sortWorkouts = (items) => {
		return [...items].sort((left, right) => {
			const muscleCompare = String(left.MUSCLE_NAME || '').localeCompare(String(right.MUSCLE_NAME || ''), 'cs');

			if (muscleCompare !== 0) {
				return muscleCompare;
			}

			const orderCompare = (parseInt(left.REC_ORDER, 10) || 0) - (parseInt(right.REC_ORDER, 10) || 0);

			if (orderCompare !== 0) {
				return orderCompare;
			}

			return String(left.NAME || '').localeCompare(String(right.NAME || ''), 'cs');
		});
	};

	const handleGroupReorder = (muscleId, reorderedItems) => {
		const reorderedMap = reorderedItems.reduce((accumulator, item) => {
			accumulator[item.ID] = item;
			return accumulator;
		}, {});

		setData((previousData) => {
			const safePrevious = Array.isArray(previousData) ? previousData : [];
			const nextData = safePrevious.map((item) => {
				if (parseInt(item.MUSCLEID, 10) === parseInt(muscleId, 10) && reorderedMap[item.ID]) {
					return reorderedMap[item.ID];
				}

				return item;
			});

			return sortWorkouts(nextData);
		});

		const payload = {
			MUSCLEID: muscleId,
			WORKOUT_IDS: reorderedItems.map((item) => item.ID),
		};

		if (targetUsrId > 0) {
			payload.USRID = targetUsrId;
		}

		post('saveWorkoutOrder', payload)
			.then((res) => {
				applyWorkoutList(res);
			})
			.catch((error) => {
				console.error(error);
				getData('', targetUsrId);
			});
	};

	const normalizedSearchValue = searchValue.trim().toLowerCase();
	const canReorder = normalizedSearchValue === '';
	const safeData = Array.isArray(data) ? data : [];
	const workoutMusclesMap = getWorkoutMusclesMap(safeData);
	const filteredData = normalizedSearchValue === ''
		? safeData
		: safeData.filter((item) => item.NAME.toLowerCase().includes(normalizedSearchValue));

	const groupedData = filteredData.reduce((groups, item) => {
		const lastGroup = groups[groups.length - 1];

		if (lastGroup && lastGroup.muscleId === item.MUSCLEID) {
			lastGroup.items.push(item);
			return groups;
		}

		groups.push({
			muscleId: item.MUSCLEID,
			muscleName: item.MUSCLE_NAME,
			accentColor: item.COLOR,
			items: [item],
		});

		return groups;
	}, []);

	const handleDragStart = (event, muscleId, index, groupItems) => {
		if (!canReorder) {
			return;
		}

		event.preventDefault();
		event.stopPropagation();

		clearDragListeners();

		const groupKey = getGroupKey(muscleId);
		const pointerId = event.pointerId;

		const onMove = (moveEvent) => {
			const currentDragState = dragStateRef.current;

			if (!currentDragState || currentDragState.pointerId !== pointerId) {
				return;
			}

			moveEvent.preventDefault();

			const overIndex = getIndexFromPointer(moveEvent.clientY, rowRefs.current[groupKey] || []);

			if (currentDragState.overIndex === overIndex) {
				return;
			}

			const nextDragState = {
				...currentDragState,
				overIndex,
			};

			dragStateRef.current = nextDragState;
			setDragState(nextDragState);
		};

		const onUp = (upEvent) => {
			const currentDragState = dragStateRef.current;

			if (!currentDragState || currentDragState.pointerId !== pointerId) {
				return;
			}

			clearDragListeners();

			if (currentDragState.fromIndex !== currentDragState.overIndex) {
				const reorderedItems = reorderList(currentDragState.items, currentDragState.fromIndex, currentDragState.overIndex);
				handleGroupReorder(muscleId, reorderedItems);
			}

			dragStateRef.current = null;
			setDragState(null);
		};

		dragListenersRef.current = {onMove, onUp};
		window.addEventListener('pointermove', onMove, {passive: false});
		window.addEventListener('pointerup', onUp);
		window.addEventListener('pointercancel', onUp);

		const nextDragState = {
			muscleId,
			fromIndex: index,
			overIndex: index,
			pointerId,
			items: groupItems,
		};

		dragStateRef.current = nextDragState;
		setDragState(nextDragState);
	};

	const list = groupedData.map((group) => {
		const groupKey = getGroupKey(group.muscleId);
		const displayItems = getGroupDisplayItems(group, dragState);
		const draggedItemId = dragState?.muscleId === group.muscleId ? group.items[dragState.fromIndex]?.ID : null;
		const isGroupDragging = dragState?.muscleId === group.muscleId;

		rowRefs.current[groupKey] = new Array(displayItems.length);

		return (
			<section className="workout-page__section" key={`group_${group.muscleId}`} style={{'--workout-accent': group.accentColor || '#8acaa3'}}>
				<div className="workout-page__section-head">
					<div className="workout-page__section-head-main">
						<div className="workout-page__section-kicker">Svalová partie</div>
						<h2 className="workout-page__section-title">{group.muscleName}</h2>
					</div>
					<div className="workout-page__section-total">{formatGroupCount(group.items.length)}</div>
				</div>

				<div className={`workout-page__list${isGroupDragging ? ' workout-page__list--dragging' : ''}`}>
					{displayItems.map((item, index) => (
						<article
							className={`workout-page__row${item.ID === draggedItemId ? ' is-dragging' : ''}`}
							key={`item_${item.ID}`}
							ref={(element) => {
								rowRefs.current[groupKey][index] = element;
							}}
						>
							<div className="workout-page__row-main">
								<button type="button" className="workout-page__icon-button" onClick={() => handleEdit(item)} aria-label={`Upravit cvik ${formatWorkoutName(item)}`}>
									<img className="icon1 edit" alt="" />
								</button>
								<div className="workout-page__row-text">
									<div className="workout-page__row-name">
										{formatWorkoutName(item)}
										<MultiMuscleBadge muscles={workoutMusclesMap[item.ID]} currentMuscleId={item.MUSCLEID} />
									</div>
								</div>
							</div>
							{canReorder ? (
								<button
									type="button"
									className="workout-page__drag-handle"
									aria-label={`Přesunout cvik ${formatWorkoutName(item)}`}
									onPointerDown={(event) => handleDragStart(event, group.muscleId, group.items.findIndex((groupItem) => groupItem.ID === item.ID), group.items)}
								>
									<span className="workout-page__drag-handle-col">
										<span />
										<span />
										<span />
									</span>
									<span className="workout-page__drag-handle-col">
										<span />
										<span />
										<span />
									</span>
								</button>
							) : (
								''
							)}
						</article>
					))}
				</div>
			</section>
		);
	});

	return (
		<>
			<div className="page_main">
				<AppHeader usrInfo={usrInfo} timerCnt={timerCnt} loading={loading} />
				<ConfirmPopup
					showConfirmPopup={showImportPopup}
					closeConfirmPopup={closeImportPopup}
					infoHeader="Přetáhnout cviky"
					infoMain=""
					popupContentClassName="workout-import-popup reservation-popup__content"
					bodyClassName="workout-import-popup__body popup_content_in_body--hidden-scrollbar"
					bodyInnerClassName="workout-import-popup__body-inner"
					footerChildren={(
						<button type="button" className="button green mr_2 workout-import-popup__submit" onClick={handleImport} disabled={importSaving || selectedSourceIds.length === 0}>
							Přidat
						</button>
					)}
				>
					<div className="workout-import-popup__content">
						<div className="workout-import-popup__search-wrap">
							<input
								className="input-text workout-import-popup__search"
								type="text"
								placeholder="Vyhledat cvik"
								value={importSearchValue}
								onChange={(e) => setImportSearchValue(e.target.value)}
								disabled={importSaving}
							/>
						</div>

						<div className="workout-import-popup__list">
							{importLoading ? <div className="workout-import-popup__empty">Načítám cviky.</div> : ''}
							{!importLoading && filteredImportData.length === 0 ? <div className="workout-import-popup__empty">Žádný cvik neodpovídá hledání.</div> : ''}
							{!importLoading ? groupedImportData.map((group) => (
								<section className="workout-import-popup__group" key={`import_group_${group.muscleId}_${group.muscleName}`} style={{'--workout-import-accent': group.accentColor || '#8acaa3'}}>
									{(() => {
										const groupWorkoutIds = group.items
											.map((item) => parseInt(item.ID, 10) || 0)
											.filter((workoutId) => workoutId > 0);
										const areAllGroupItemsSelected = groupWorkoutIds.length > 0 && groupWorkoutIds.every((workoutId) => Boolean(selectedImportIds[workoutId]));

										return (
									<div className="workout-import-popup__group-head">
										<div className="workout-import-popup__group-toggle-wrap">
											<button
												type="button"
												className={`button check workout-import-popup__group-toggle${areAllGroupItemsSelected ? ' green' : ''}`}
												disabled={importSaving}
												onClick={() => handleImportGroupToggle(group.items)}
												aria-label={areAllGroupItemsSelected ? `Odebrat všechny cviky ze svalové partie ${group.muscleName}` : `Vybrat všechny cviky ze svalové partie ${group.muscleName}`}
											>
												&nbsp;
											</button>
										</div>
										<div className="workout-import-popup__group-head-main">
											<div className="workout-import-popup__group-kicker">Svalová partie</div>
											<div className="workout-import-popup__group-title">{group.muscleName}</div>
										</div>
										<div className="workout-import-popup__group-total">{formatGroupCount(group.items.length)}</div>
									</div>
										);
									})()}

									<div className="workout-import-popup__group-list">
										{group.items.map((item) => {
											const workoutId = parseInt(item.ID, 10) || 0;
											const isSelected = Boolean(selectedImportIds[workoutId]);

											return (
												<div className={`workout-import-popup__row${isSelected ? ' is-selected' : ''}`} key={workoutId} onClick={() => handleImportRowToggle(workoutId)}>
													<button type="button" className={`button check ${isSelected ? 'green' : ''}`} disabled={importSaving} aria-label={isSelected ? `Odebrat ${item.NAME}` : `Vybrat ${item.NAME}`}>
														&nbsp;
													</button>
													<div className="workout-import-popup__row-text">
														<div className="workout-import-popup__row-name">
															{item.NAME}
															<MultiMuscleBadge muscles={importMusclesMap[item.ID]} currentMuscleId={item.MUSCLEID} />
														</div>
													</div>
												</div>
											);
										})}
									</div>
								</section>
							)) : ''}
						</div>
					</div>
				</ConfirmPopup>

				<Search showSearch searchValue={searchValue} onChangeSearch={(value) => onChangeSearch(value)} showNew handleNew={handleNew} debounceMs={0} actionButtons={isInstructorClientView ? (
					<button type="button" className="button small search-new-button" onClick={openImportPopup}>
						Přetáhnout
					</button>
				) : null} />
				<WorkoutNew showNew={showNew} closeNew={closeNew} updateWorkoutRowNew={updateWorkoutRowNew} dataMuscles={dataMuscles} dataWorkoutOffic={dataWorkoutOffic} targetUsrId={targetUsrId} />
				<WorkoutEdit showEdit={showEdit} closeEdit={closeEdit} updateWorkoutRowEdit={updateWorkoutRowEdit} updateWorkoutRowDelete={updateWorkoutRowDelete} data={dataDetail} dataMuscles={dataMuscles} dataWorkoutOffic={dataWorkoutOffic} targetUsrId={targetUsrId} />

				<div className={`page workout-page${usrInfo.sysAdmin === 1 || usrInfo.manager === 1 || usrInfo.instructor === 1 ? ' more' : ''}`}>
					<section className="workout-page__hero">
						<div className="workout-page__hero-main">
							<h1 className="workout-page__title">Cviky</h1>
							{isInstructorClientView && targetUsrName !== '' ? <div className="workout-page__client-name">{targetUsrName}</div> : null}
						</div>
					</section>
					<div className="workout-page__shell">
						{list.length > 0 ? list : <div className="workout-page__empty">Zatím nejsou uložené žádné cviky.</div>}
					</div>
				</div>
			</div>
		</>
	);
}
